import React, { useState } from 'react';
import { User, Percent, Trash2, Plus, Search } from 'lucide-react';

export interface EmployeeAssignment {
  user_id: number;
  commission_percentage: number;
}

export interface UserOption {
  id: number;
  name: string;
  email?: string;
}

interface EmployeePercentageSelectorProps {
  employees: EmployeeAssignment[];
  onChange: (employees: EmployeeAssignment[]) => void;
  availableUsers: UserOption[];
  loading?: boolean;
  error?: string | null;
}

export const EmployeePercentageSelector: React.FC<EmployeePercentageSelectorProps> = ({
  employees,
  onChange,
  availableUsers,
  loading = false,
  error = null
}) => {
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const totalPercentage = employees.reduce((sum, emp) =>
    sum + Number(emp.commission_percentage || 0), 0
  );

  const allPercentagesValid = employees.every(emp => Number(emp.commission_percentage || 0) > 0);

  const addEmployee = (userId: number) => {
    const remaining = 100 - totalPercentage;
    const newPercentage = remaining > 0 ? remaining : 0;

    onChange([...employees, {
      user_id: userId,
      commission_percentage: Number(newPercentage.toFixed(2))
    }]);

    setShowUserSelect(false);
    setSearchTerm('');
  };

  const updatePercentage = (index: number, percentage: number) => {
    const updated = [...employees];
    updated[index].commission_percentage = Number(percentage);
    onChange(updated);
  };

  const removeEmployee = (index: number) => {
    onChange(employees.filter((_, i) => i !== index));
  };

  const getAvailableUsersFiltered = () => {
    const assignedUserIds = employees.map(e => e.user_id);
    const available = availableUsers.filter(u => !assignedUserIds.includes(u.id));

    if (!searchTerm.trim()) {
      return available;
    }

    const term = searchTerm.toLowerCase();
    return available.filter(u =>
      u.name.toLowerCase().includes(term) ||
      (u.email && u.email.toLowerCase().includes(term))
    );
  };

  const filteredUsers = getAvailableUsersFiltered();

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Employees & Commission %
          </label>
          <span className="text-xs text-gray-500">
            {employees.length} employee{employees.length !== 1 ? 's' : ''} assigned
          </span>
        </div>

        {/* Employee List */}
        {employees.length > 0 && (
          <div className="space-y-2">
            {employees.map((emp, index) => {
              const user = availableUsers.find(u => u.id === emp.user_id);
              return (
                <div
                  key={`${emp.user_id}-${index}`}
                  className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user?.name || `User #${emp.user_id}`}
                    </div>
                    {user?.email && (
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <input
                      type="number"
                      value={emp.commission_percentage}
                      onChange={(e) => updatePercentage(index, Number(e.target.value))}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    <Percent className="h-4 w-4 text-gray-400" />
                    <button
                      type="button"
                      onClick={() => removeEmployee(index)}
                      className="text-red-600 hover:text-red-800 transition-colors p-1"
                      title="Remove employee"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Employee Section */}
        {!showUserSelect ? (
          <button
            type="button"
            onClick={() => setShowUserSelect(true)}
            className="btn btn-secondary w-full flex items-center justify-center"
            disabled={availableUsers.length === employees.length}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Employee
          </button>
        ) : (
          <div className="border border-gray-300 rounded-lg p-4 bg-white">
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => addEmployee(user.id)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-lg transition-colors flex items-center space-x-2 group"
                  >
                    <User className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {user.name}
                      </div>
                      {user.email && (
                        <div className="text-xs text-gray-500 truncate">{user.email}</div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-4 text-sm text-gray-500">
                  {searchTerm ? 'No employees found' : 'All employees assigned'}
                </div>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowUserSelect(false);
                  setSearchTerm('');
                }}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Total Percentage Indicator */}
      {employees.length > 0 && (
        <div className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
          allPercentagesValid
            ? 'bg-green-50 border-green-300'
            : 'bg-red-50 border-red-300'
        }`}>
          <span className="font-medium text-gray-700">Total Assigned:</span>
          <div className="flex items-center space-x-2">
            <span className={`text-lg font-bold ${
              allPercentagesValid ? 'text-green-700' : 'text-red-700'
            }`}>
              {totalPercentage.toFixed(2)}%
            </span>
            {!allPercentagesValid && (
              <span className="text-sm text-red-600">Each employee must be &gt; 0%</span>
            )}
          </div>
        </div>
      )}

    </div>
  );
};
